import React, { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Placeholder } from '@tiptap/extensions';
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Quote, Undo2, Redo2, RemoveFormatting, AlignLeft, AlignCenter, Minus, Heading2, Palette } from 'lucide-react';
import './richEditor.css';

const COLORS = ['#0f172a', '#dc2626', '#d97706', '#16a34a', '#2563eb', '#7c3aed', '#64748b'];
const MAX_W = 1200;

/** Downscale big pastes/drops so emails stay light; returns a data: URL. */
export const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onerror = reject;
  fr.onload = () => {
    const img = new window.Image();
    img.onload = () => {
      if (img.width <= MAX_W || !/^image\/(png|jpe?g|webp)$/i.test(file.type)) return resolve(fr.result);
      const c = document.createElement('canvas');
      const k = MAX_W / img.width;
      c.width = MAX_W; c.height = Math.round(img.height * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.88));
    };
    img.onerror = () => resolve(fr.result);
    img.src = fr.result;
  };
  fr.readAsDataURL(file);
});

const TBtn = ({ onClick, active, title, children, testid, disabled }) => (
  <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClick} disabled={disabled} title={title} data-testid={testid}
    className={`p-1.5 rounded-md transition-colors disabled:opacity-30 ${active ? 'bg-sky-100 text-sky-800' : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-900'}`}>{children}</button>
);

/** Rich-text mail body (TipTap): bold/italic/lists/links/inline images (paste, drop or pick) — emits HTML. */
const RichEditor = forwardRef(function RichEditor({ value, onChange, placeholder = 'Write your message…', autoFocus = false, minHeight = 180, testid = 'rich-editor' }, ref) {
  const fileRef = useRef(null);
  const [showColors, setShowColors] = React.useState(false);
  const last = useRef(value || '');

  const insertFiles = useCallback(async (ed, files) => {
    for (const f of Array.from(files || []).filter(f => f.type.startsWith('image/'))) {
      if (f.size > 8 * 1024 * 1024) continue;
      const src = await fileToDataUrl(f);
      ed.chain().focus().setImage({ src, alt: f.name }).run();
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true, defaultProtocol: 'https' } }),
      Image.configure({ allowBase64: true, inline: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle, Color,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: { class: 'rich-mail-body', 'data-testid': testid, style: `min-height:${minHeight}px` },
      handlePaste: (view, event) => {
        const files = Array.from(event.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
        if (files.length) { event.preventDefault(); insertFiles(editorRef.current, files); return true; }
        return false;
      },
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
        if (files.length) { event.preventDefault(); insertFiles(editorRef.current, files); return true; }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => { const html = ed.isEmpty ? '' : ed.getHTML(); last.current = html; onChange?.(html); },
  });
  const editorRef = useRef(editor);
  useEffect(() => { editorRef.current = editor; }, [editor]);

  // external value changes (template insert, opening a draft) → update content without clobbering the caret during typing
  useEffect(() => {
    if (!editor || value === last.current) return;
    last.current = value || '';
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  useImperativeHandle(ref, () => ({
    insertHtml: (html) => editor?.chain().focus().insertContent(html).run(),
    appendHtml: (html) => editor?.chain().focus('end').insertContent(html).run(),
    focus: () => editor?.commands.focus('end'),
    getHTML: () => (editor && !editor.isEmpty ? editor.getHTML() : ''),
    getText: () => editor?.getText() || '',
    isEmpty: () => !editor || editor.isEmpty,
  }), [editor]);

  if (!editor) return null;
  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link to (URL):', prev || 'https://');
    if (url === null) return;
    if (!url.trim() || url === 'https://') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: /^https?:|^mailto:/i.test(url) ? url : `https://${url}` }).run();
  };
  return (
    <div className="rich-editor rounded-xl border border-slate-200 bg-white focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100 transition-shadow" data-testid={`${testid}-wrap`}>
      <div className="flex items-center gap-0.5 flex-wrap px-1.5 py-1 border-b border-slate-100 bg-slate-50/70 rounded-t-xl" data-testid={`${testid}-toolbar`}>
        <TBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)" testid="re-bold"><Bold className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)" testid="re-italic"><Italic className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)" testid="re-underline"><Underline className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" testid="re-strike"><Strikethrough className="w-4 h-4" /></TBtn>
        <div className="relative">
          <TBtn onClick={() => setShowColors(s => !s)} title="Text colour" testid="re-color"><Palette className="w-4 h-4" /></TBtn>
          {showColors && (
            <div className="absolute z-20 top-8 left-0 flex gap-1 p-1.5 rounded-lg bg-white border border-slate-200 shadow-lg" onMouseDown={e => e.preventDefault()}>
              {COLORS.map(c => <button key={c} type="button" onClick={() => { editor.chain().focus().setColor(c).run(); setShowColors(false); }} className="h-5 w-5 rounded-full border border-slate-200" style={{ background: c }} data-testid={`re-color-${c.slice(1)}`} />)}
              <button type="button" onClick={() => { editor.chain().focus().unsetColor().run(); setShowColors(false); }} className="text-[10px] px-1 text-slate-500">auto</button>
            </div>
          )}
        </div>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <TBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading" testid="re-heading"><Heading2 className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list" testid="re-bullets"><List className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list" testid="re-numbers"><ListOrdered className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote" testid="re-quote"><Quote className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().setTextAlign(editor.isActive({ textAlign: 'center' }) ? 'left' : 'center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centre" testid="re-center">{editor.isActive({ textAlign: 'center' }) ? <AlignCenter className="w-4 h-4" /> : <AlignLeft className="w-4 h-4" />}</TBtn>
        <TBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider" testid="re-hr"><Minus className="w-4 h-4" /></TBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <TBtn onClick={setLink} active={editor.isActive('link')} title="Link (Ctrl+K)" testid="re-link"><LinkIcon className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => fileRef.current?.click()} title="Insert image (or paste / drop one)" testid="re-image"><ImageIcon className="w-4 h-4" /></TBtn>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { insertFiles(editor, e.target.files); e.target.value = ''; }} data-testid="re-image-input" />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <TBtn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testid="re-clear"><RemoveFormatting className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid="re-undo"><Undo2 className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid="re-redo"><Redo2 className="w-4 h-4" /></TBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});

export default RichEditor;
